import { useState, useEffect, useRef, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useApp } from "../context/AppContext";

export default function GscView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  const [gscStatus, setGscStatus] = useState(null); // null=loading, true/false
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [days, setDays] = useState(28);
  const [gscData, setGscData] = useState([]);
  const [gscLoading, setGscLoading] = useState(false);
  const [sortCol, setSortCol] = useState("clicks");
  const [sortDir, setSortDir] = useState("desc");
  const [gscError, setGscError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [showPasteField, setShowPasteField] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [pasteLoading, setPasteLoading] = useState(false);
  const [authState, setAuthState] = useState("");
  const [minImpressions, setMinImpressions] = useState(10);
  const [loadProgress, setLoadProgress] = useState(null); // { page, fetched, filtering }
  const [tablePage, setTablePage] = useState(0);
  const TABLE_PAGE_SIZE = 200;
  const [siteSearch, setSiteSearch] = useState("");
  const [siteDropOpen, setSiteDropOpen] = useState(false);
  const siteDropRef = useRef(null);
  const [dailyData, setDailyData] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [chartMetric, setChartMetric] = useState("both"); // "clicks"|"impressions"|"both"
  const [gscSearch, setGscSearch] = useState("");
  const [gscPageFilter, setGscPageFilter] = useState("");
  const [gscTab, setGscTab] = useState("queries");

  const token = localStorage.getItem("metago_token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const onGscConnected = () => {
    setGscStatus(true);
    setShowPasteField(false);
    setPasteUrl("");
    setAuthState("");
    fetch(`/api/gsc/sites?projectId=${activeId}`, { headers }).then(r => r.json()).then(s => {
      if (s.sites) setSites(s.sites);
    });
  };

  const loadDailyChart = (d = days) => {
    setDailyLoading(true);
    fetch(`/api/gsc/daily/${activeId}?days=${d}`, { headers })
      .then(r => r.ok ? r.json() : { rows: [] })
      .then(d => setDailyData(d.rows || []))
      .catch(() => {})
      .finally(() => setDailyLoading(false));
  };

  // Load cached daily chart (no live API call)
  const loadDailyFromCache = (d = days) => {
    fetch(`/api/gsc/daily-cache/${activeId}?days=${d}`, { headers })
      .then(r => r.ok ? r.json() : { rows: [] })
      .then(d => { if ((d.rows || []).length) setDailyData(d.rows); })
      .catch(() => {});
  };

  // Load saved data + check GSC status
  useEffect(() => {
    if (activeId) {
      fetch(`/api/gsc/saved/${activeId}`, { headers }).then(r => r.json()).then(d => {
        if (d.found) {
          setGscData(d.rows || []);
          setSelectedSite(d.siteUrl || "");
          setDays(d.days || 28);
          setSavedAt(d.updatedAt);
        }
      }).catch(() => {});
      loadDailyFromCache();
    }

    fetch(`/api/gsc/status?projectId=${activeId}`, { headers }).then(r => r.json()).then(d => {
      setGscStatus(d.connected);
      if (d.connected) {
        fetch(`/api/gsc/sites?projectId=${activeId}`, { headers }).then(r => r.json()).then(s => {
          if (s.sites) setSites(s.sites);
          if (s.error) { setGscError(s.error); setGscStatus(false); }
        });
      }
    }).catch(() => setGscStatus(false));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "GSC_CONNECTED") onGscConnected();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (siteDropRef.current && !siteDropRef.current.contains(e.target)) setSiteDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);


  const connectGsc = () => {
    fetch(`/api/gsc/auth-url?projectId=${activeId}`, { headers }).then(r => r.json()).then(d => {
      // Save the state from the auth URL for manual exchange
      try {
        const u = new URL(d.url);
        setAuthState(u.searchParams.get("state") || "");
      } catch {}
      setShowPasteField(true);
      window.open(d.url, "gsc-auth", "width=600,height=700");
    });
  };

  const handlePasteExchange = () => {
    if (!pasteUrl.trim()) return;
    setPasteLoading(true);
    setGscError("");
    try {
      const u = new URL(pasteUrl.trim());
      const code = u.searchParams.get("code");
      const state = u.searchParams.get("state") || authState;
      if (!code) { setGscError("URL не содержит code параметр"); setPasteLoading(false); return; }
      if (!state) { setGscError("Отсутствует state параметр"); setPasteLoading(false); return; }
      fetch("/api/gsc/exchange", {
        method: "POST", headers,
        body: JSON.stringify({ code, state }),
      }).then(r => r.json()).then(d => {
        if (d.ok) onGscConnected();
        else setGscError(d.error || "Ошибка обмена кода");
      }).catch(e => setGscError(e.message)).finally(() => setPasteLoading(false));
    } catch {
      setGscError("Некорректный URL");
      setPasteLoading(false);
    }
  };

  const fetchData = async () => {
    if (!selectedSite) return;
    setGscLoading(true);
    setGscError("");
    setLoadProgress({ page: 0, fetched: 0 });
    setTablePage(0);
    try {
      const resp = await fetch("/api/gsc/query", {
        method: "POST", headers,
        body: JSON.stringify({ siteUrl: selectedSite, days, projectId: activeId, minImpressions }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const msg = JSON.parse(line.slice(6));
            if (msg.type === "progress") {
              setLoadProgress({ page: msg.page, fetched: msg.fetched, filtering: msg.filtering });
            } else if (msg.type === "done") {
              setGscData(msg.rows || []);
              setSavedAt(new Date().toISOString());
              setLoadProgress(null);
              loadDailyChart(days);
            } else if (msg.type === "error") {
              setGscError(msg.error);
              if (msg.error.includes("reconnect")) setGscStatus(false);
              setLoadProgress(null);
            }
          } catch {}
        }
      }
    } catch (e) {
      setGscError(e.message);
      setLoadProgress(null);
    } finally {
      setGscLoading(false);
    }
  };

  const sorted = useMemo(() => {
    let data = gscData;
    if (gscSearch) {
      const q = gscSearch.toLowerCase();
      data = data.filter(r => r.query.toLowerCase().includes(q) || (r.page || "").toLowerCase().includes(q));
    }
    if (gscPageFilter) {
      const pf = gscPageFilter.toLowerCase();
      data = data.filter(r => (r.page || "").toLowerCase().includes(pf));
    }
    return [...data].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [gscData, sortCol, sortDir, gscSearch, gscPageFilter]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const thStyle = { padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: t.textMuted, cursor: "pointer", userSelect: "none", borderBottom: `2px solid ${t.border}` };
  const tdStyle = { padding: "8px 12px", fontSize: 13, borderBottom: `1px solid ${t.border}` };
  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Google Search Console</h2>
        {gscStatus && selectedSite && (
          <button onClick={async () => {
            try {
              await apiFetch("/api/gsc/coverage-check", {
                method: "POST",
                body: JSON.stringify({ projectId: activeId, siteUrl: selectedSite }),
              });
              flash("Перевірка індексації запущена (фонова задача)");
            } catch (e) { flash("Помилка: " + e.message); }
          }} style={{ ...btnG, marginLeft: "auto", fontSize: 12 }}>📄 Перевірити індексацію</button>
        )}
      </div>

      {gscStatus === true && (
        <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `2px solid ${t.border}` }}>
          {[{ k: "queries", l: "Запити" }].map(tab => (
            <button
              key={tab.k}
              onClick={() => { setGscTab(tab.k); }}
              style={{
                padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: "transparent", border: "none", color: gscTab === tab.k ? "#6366f1" : t.textMuted,
                borderBottom: gscTab === tab.k ? "2px solid #6366f1" : "2px solid transparent",
                marginBottom: -2, fontFamily: "inherit",
              }}
            >{tab.l}</button>
          ))}
        </div>
      )}

      {gscStatus === null && <p style={{ color: t.textMuted, fontSize: 13 }}>Проверка подключения...</p>}

      {gscStatus === false && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>GSC не подключен</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>Подключите Google Search Console для импорта данных о поисковых запросах</p>
          <button style={btnP} onClick={connectGsc}>Подключить GSC</button>
          {showPasteField && (
            <div style={{ marginTop: 20, textAlign: "left", maxWidth: 500, margin: "20px auto 0" }}>
              <p style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>
                Если окно авторизации не закрылось автоматически — скопируйте URL из адресной строки окна авторизации и вставьте сюда:
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  value={pasteUrl}
                  onChange={e => setPasteUrl(e.target.value)}
                  placeholder="http://localhost:3000/api/gsc/callback?code=...&state=..."
                  style={{ flex: 1, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, background: t.bgCard, color: t.text, fontFamily: "inherit" }}
                />
                <button style={btnP} onClick={handlePasteExchange} disabled={pasteLoading || !pasteUrl.trim()}>
                  {pasteLoading ? "..." : "Подключить"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {gscError && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{gscError}</div>}

      {gscStatus === true && gscTab === "queries" && (
        <div>
          <div style={{ ...card, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", position: "relative", zIndex: siteDropOpen ? 9999 : 1, overflow: "visible" }}>
            <div style={{ position: "relative", zIndex: siteDropOpen ? 9999 : "auto" }} ref={siteDropRef}>
              <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Сайт</label>
              <div
                onClick={() => setSiteDropOpen(!siteDropOpen)}
                style={{ border: `1px solid ${siteDropOpen ? "#6366f1" : t.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 13, background: t.bgCard, color: selectedSite ? t.text : t.textMuted, fontFamily: "inherit", minWidth: 250, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedSite || "Выберите сайт"}</span>
                <span style={{ fontSize: 10, flexShrink: 0 }}>{siteDropOpen ? "▲" : "▼"}</span>
              </div>
              {siteDropOpen && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.15)", zIndex: 9999, maxHeight: 300, display: "flex", flexDirection: "column" }}>
                  <div style={{ padding: 8, borderBottom: `1px solid ${t.border}` }}>
                    <input
                      autoFocus
                      type="text"
                      value={siteSearch}
                      onChange={e => setSiteSearch(e.target.value)}
                      placeholder="Поиск сайта..."
                      onClick={e => e.stopPropagation()}
                      style={{ width: "100%", border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 8px", fontSize: 13, background: t.bg, color: t.text, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ overflowY: "auto", maxHeight: 240 }}>
                    {sites.filter(s => !siteSearch || s.siteUrl.toLowerCase().includes(siteSearch.toLowerCase())).map(s => (
                      <div
                        key={s.siteUrl}
                        onClick={() => { setSelectedSite(s.siteUrl); setSiteDropOpen(false); setSiteSearch(""); }}
                        style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: s.siteUrl === selectedSite ? (dark ? "rgba(99,102,241,.2)" : "rgba(99,102,241,.1)") : "transparent", color: t.text, borderBottom: `1px solid ${t.border}` }}
                        onMouseEnter={e => { if (s.siteUrl !== selectedSite) e.currentTarget.style.background = dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.03)"; }}
                        onMouseLeave={e => { if (s.siteUrl !== selectedSite) e.currentTarget.style.background = "transparent"; }}
                      >
                        {s.siteUrl}
                      </div>
                    ))}
                    {sites.filter(s => !siteSearch || s.siteUrl.toLowerCase().includes(siteSearch.toLowerCase())).length === 0 && (
                      <div style={{ padding: "12px", fontSize: 12, color: t.textMuted, textAlign: "center" }}>Ничего не найдено</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Период</label>
              <select
                value={days} onChange={e => setDays(Number(e.target.value))}
                style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 13, background: t.bgCard, color: t.text, fontFamily: "inherit" }}
              >
                <option value={7}>7 дней</option>
                <option value={28}>28 дней</option>
                <option value={90}>90 дней</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Мин. показы</label>
              <input
                type="number" min={1} value={minImpressions}
                onChange={e => setMinImpressions(Math.max(1, parseInt(e.target.value) || 1))}
                style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 13, background: t.bgCard, color: t.text, fontFamily: "inherit", width: 80 }}
              />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <button style={btnP} onClick={fetchData} disabled={!selectedSite || gscLoading}>
                {gscLoading ? "Загрузка..." : "Получить данные"}
              </button>
            </div>
            <div style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
              <button style={{ ...btnSm, color: "#ef4444" }} onClick={() => { setGscStatus(false); setGscData([]); setSites([]); }}>
                Отключить
              </button>
            </div>
          </div>

          {loadProgress && (
            <div style={{ ...card, padding: "16px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 20, height: 20, border: "3px solid " + t.border, borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {loadProgress.filtering ? "Фильтрация данных..." : `Загрузка страницы ${loadProgress.page}...`}
                  </div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>
                    Получено {loadProgress.fetched.toLocaleString()} строк из GSC
                  </div>
                </div>
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Daily Chart */}
          {dailyData.length > 0 && (() => {
            const totalClicks = dailyData.reduce((s, d) => s + d.clicks, 0);
            const totalImpr = dailyData.reduce((s, d) => s + d.impressions, 0);
            const labels = dailyData.map(d => d.date.slice(5));
            const datasets = [];
            if (chartMetric === "clicks" || chartMetric === "both") datasets.push({
              label: "Клики", data: dailyData.map(d => d.clicks), yAxisID: "y",
              borderColor: "#6366f1", backgroundColor: "rgba(99,102,241,0.1)",
              fill: true, tension: 0.4, borderWidth: 2.5, pointRadius: 2, pointHoverRadius: 5,
              pointBackgroundColor: "#fff", pointBorderColor: "#6366f1", pointBorderWidth: 2,
            });
            if (chartMetric === "impressions" || chartMetric === "both") datasets.push({
              label: "Показы", data: dailyData.map(d => d.impressions), yAxisID: chartMetric === "both" ? "y1" : "y",
              borderColor: "#f59e0b", backgroundColor: "rgba(245,158,11,0.08)",
              fill: true, tension: 0.4, borderWidth: 2.5, pointRadius: 2, pointHoverRadius: 5,
              pointBackgroundColor: "#fff", pointBorderColor: "#f59e0b", pointBorderWidth: 2,
            });
            const opts = {
              responsive: true, maintainAspectRatio: false, interaction: { mode: "index", intersect: false },
              plugins: { legend: { display: false }, tooltip: { backgroundColor: dark ? "#1e1e2e" : "#fff", titleColor: dark ? "#e5e7eb" : "#111", bodyColor: dark ? "#ccc" : "#444", borderColor: dark ? "#333" : "#e5e7eb", borderWidth: 1, padding: 10, cornerRadius: 8, titleFont: { weight: "600" } } },
              scales: {
                x: { grid: { color: dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)", drawBorder: false }, ticks: { color: dark ? "#888" : "#aaa", font: { size: 10 }, maxTicksLimit: 8 } },
                y: { position: "left", grid: { color: dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)", drawBorder: false }, ticks: { color: "#6366f1", font: { size: 10 } } },
                ...(chartMetric === "both" ? { y1: { position: "right", grid: { drawOnChartArea: false }, ticks: { color: "#f59e0b", font: { size: 10 }, callback: v => v >= 1000 ? (v/1000).toFixed(0)+"k" : v } } } : {}),
              },
            };
            return (
              <div style={{ ...card }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                    {(chartMetric === "clicks" || chartMetric === "both") && <span><span style={{ color: "#6366f1", fontWeight: 700 }}>●</span> Клики: {totalClicks.toLocaleString()}</span>}
                    {(chartMetric === "impressions" || chartMetric === "both") && <span><span style={{ color: "#f59e0b", fontWeight: 700 }}>●</span> Показы: {totalImpr.toLocaleString()}</span>}
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[{ d: 7, l: "7д" }, { d: 28, l: "28д" }, { d: 90, l: "3м" }].map(p => (
                        <button key={p.d} onClick={() => { setDays(p.d); loadDailyChart(p.d); }} style={{
                          ...btnSm, fontSize: 10, padding: "3px 8px",
                          background: days === p.d ? "#10b981" : t.bgCard,
                          color: days === p.d ? "#fff" : t.textMuted,
                          border: days === p.d ? "1px solid #10b981" : `1px solid ${t.border}`,
                        }}>{p.l}</button>
                      ))}
                    </div>
                    <div style={{ width: 1, height: 16, background: t.border }} />
                    <div style={{ display: "flex", gap: 3 }}>
                      {[{ k: "both", l: "Оба" }, { k: "clicks", l: "Клики" }, { k: "impressions", l: "Показы" }].map(o => (
                        <button key={o.k} onClick={() => setChartMetric(o.k)} style={{ ...btnSm, fontSize: 10, padding: "3px 8px", background: chartMetric === o.k ? "#6366f1" : t.bgCard, color: chartMetric === o.k ? "#fff" : t.text, border: chartMetric === o.k ? "1px solid #6366f1" : `1px solid ${t.border}` }}>{o.l}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ height: 220 }}>
                  <Line data={{ labels, datasets }} options={opts} />
                </div>
              </div>
            );
          })()}
          {dailyLoading && <div style={{ ...card, textAlign: "center", padding: 16, fontSize: 13, color: t.textMuted }}>Загрузка графика...</div>}

          {!loadProgress && sorted.length > 0 && (() => {
            const totalPages = Math.ceil(sorted.length / TABLE_PAGE_SIZE);
            const pageRows = sorted.slice(tablePage * TABLE_PAGE_SIZE, (tablePage + 1) * TABLE_PAGE_SIZE);
            return (
              <div style={{ ...card, padding: 0, overflow: "auto" }}>
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{sorted.length.toLocaleString()} запросов</span>
                  {savedAt && <span style={{ fontSize: 12, color: t.textMuted }}>Сохранено: {new Date(savedAt).toLocaleString()}</span>}
                  <input
                    type="text" placeholder="Поиск по запросу..."
                    value={gscSearch} onChange={e => { setGscSearch(e.target.value); setTablePage(0); }}
                    style={{ padding: "4px 10px", fontSize: 12, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, outline: "none", width: 180 }}
                  />
                  <input
                    type="text" placeholder="Фильтр по URL..."
                    value={gscPageFilter} onChange={e => { setGscPageFilter(e.target.value); setTablePage(0); }}
                    style={{ padding: "4px 10px", fontSize: 12, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, outline: "none", width: 180 }}
                  />
                  {(gscSearch || gscPageFilter) && <button style={{ ...btnSm, color: "#ef4444" }} onClick={() => { setGscSearch(""); setGscPageFilter(""); setTablePage(0); }}>Сбросить</button>}
                  {totalPages > 1 && (
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        style={{ ...btnSm, opacity: tablePage === 0 ? 0.4 : 1 }}
                        disabled={tablePage === 0}
                        onClick={() => setTablePage(p => p - 1)}
                      >←</button>
                      <span style={{ fontSize: 12, color: t.textMuted }}>{tablePage + 1} / {totalPages}</span>
                      <button
                        style={{ ...btnSm, opacity: tablePage >= totalPages - 1 ? 0.4 : 1 }}
                        disabled={tablePage >= totalPages - 1}
                        onClick={() => setTablePage(p => p + 1)}
                      >→</button>
                    </div>
                  )}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle} onClick={() => { toggleSort("query"); setTablePage(0); }}>Запрос{sortIcon("query")}</th>
                      <th style={{ ...thStyle, maxWidth: 250 }} onClick={() => { toggleSort("page"); setTablePage(0); }}>URL{sortIcon("page")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }} onClick={() => { toggleSort("clicks"); setTablePage(0); }}>Клики{sortIcon("clicks")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }} onClick={() => { toggleSort("impressions"); setTablePage(0); }}>Показы{sortIcon("impressions")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }} onClick={() => { toggleSort("ctr"); setTablePage(0); }}>CTR %{sortIcon("ctr")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }} onClick={() => { toggleSort("position"); setTablePage(0); }}>Позиция{sortIcon("position")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, i) => (
                      <tr key={tablePage * TABLE_PAGE_SIZE + i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                        <td style={tdStyle}>{r.query}</td>
                        <td style={{ ...tdStyle, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, color: t.textMuted }} title={r.page}>{(r.page || "").replace(/^https?:\/\/[^/]+/, "")}</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{r.clicks}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{r.impressions}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{r.ctr}%</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{r.position}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div style={{ padding: "10px 18px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "center", gap: 6 }}>
                    <button style={{ ...btnSm, opacity: tablePage === 0 ? 0.4 : 1 }} disabled={tablePage === 0} onClick={() => setTablePage(p => p - 1)}>←</button>
                    <span style={{ fontSize: 12, color: t.textMuted, lineHeight: "28px" }}>{tablePage + 1} / {totalPages}</span>
                    <button style={{ ...btnSm, opacity: tablePage >= totalPages - 1 ? 0.4 : 1 }} disabled={tablePage >= totalPages - 1} onClick={() => setTablePage(p => p + 1)}>→</button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

    </div>
  );
}
